import { useTranslation } from 'react-i18next';
import SmallLetter from '../../../../../components/SmallLetter/SmallLetter';
import { IColumnType, Table } from '../../../../../components/Table/Table';
import { IInitialState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getActionByProjectType } from '../../../../actionMap';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { getVerticalDropsType } from '../../../../utils/utils';
import { IVerticalDropsResults } from '../../../api/IApiResponse';
import { EVerticalDropsTypes } from '../../../enum/general';
import { selectStillingBasinAndSoilDataByProject } from '../../../selector/conversion';
import { IData } from '../HydraulicResults/HydraulicVertical';
import '../StaticResults/StaticResults.scss';

interface IBasinDesignVerticalProps {}

const BasinDesignVertical = (props: IBasinDesignVerticalProps) => {
    const { t } = useTranslation();
    const { selectResults, selectProject } = getActionByProjectType();
    const results: IInitialState<IVerticalDropsResults> = useAppSelector(selectResults as any);
    const stillingBasinSoilData = useAppSelector(selectStillingBasinAndSoilDataByProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    const type: EVerticalDropsTypes = getVerticalDropsType(stillingBasinSoilData.stillingBasin);
    const columnsInput: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_static_vertical_stepped_basin_design').toUpperCase(),
        },
        {
            key: 'symbol',
            title: t('output_symbol'),
        },
        {
            key: 'value',
            title: t('output_value'),
        },
        {
            key: 'unit',
            title: t('output_unit'),
        },
        {
            key: 'check',
            title: t('output_check'),
            render: (_, row) => {
                if (typeof row.check === 'boolean') {
                    if (row.check)
                        return (
                            <div className='d-flex justify-content-center'>
                                <i className='fa-solid fa-check' style={{ color: '#004652' }} />
                            </div>
                        );
                    else
                        return (
                            <div className='d-flex justify-content-center'>
                                <i className='fa-solid fa-x' style={{ color: '#E31937' }} />
                            </div>
                        );
                } else {
                    return <div className='d-flex justify-content-center'>-</div>;
                }
            },
        },
    ];
    const dissipationBasin: IData[] = [
        {
            label: t('output_hydraulic_vertical_location_hydraulic_jump_start'),
            symbol: 'Lg1',
            value: roundToTwo(results.data?.hydraulicResult.locationOfHydraulicJumpStart),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
            check: undefined,
        },
        {
            label: t('output_hydraulic_vertical_min_basin_length'),
            symbol: <SmallLetter normal='L' small='bas_min' />,
            value: roundToTwo(results.data?.hydraulicResult.minimumBasinLengthUsace1990),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
            check: stillingBasinSoilData.stillingBasin?.poolLength! >= roundToTwo(results.data?.hydraulicResult.minimumBasinLengthUsace1990),
        },
    ];
    const scourLocation: IData[] = [
        {
            label: t('output_hydraulic_vertical_scour_location'),
            symbol: 'X',
            value: roundToTwo(results.data?.hydraulicResult.scourLocation),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
            check: undefined,
        },
    ];

    const materialAndStress: IData[] = [
        {
            label: t('output_hydraulic_vertical_stepped_sloped_lining_material'),
            symbol: '-',
            value: `${stillingBasinSoilData.stillingBasin?.material.name!} - t=${roundToTwo(stillingBasinSoilData.stillingBasin?.material.physicalData![0].thickness)} 
            ${conversionLabel.m_in[projectInfo.data?.unitOfMeasure as number]} - d50=
            ${roundToTwo(stillingBasinSoilData.stillingBasin?.material.physicalData![0].rockD50)} ${conversionLabel.m_in[projectInfo.data?.unitOfMeasure as number]} - Cu=
            ${roundToTwo(stillingBasinSoilData.stillingBasin?.material.physicalData![0].cu)}`,
            unit: '-',
            check: undefined,
            valueFullRow: true,
        },
        {
            label: t('output_hydraulic_sloped_basin_lining_thickness'),
            symbol: <SmallLetter normal='t' small='b' />,
            value: roundToTwo(stillingBasinSoilData.stillingBasin?.thickness),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
            check: undefined,
        },
        {
            label: t('output_hydraulic_vertical_stepped_sloped_shear_stress_downstream_section'),
            symbol: <SmallLetter normal='τ' small='bas_calc' />,
            value: roundToTwo(results.data?.hydraulicResult.shearStressInTheBasin),
            unit: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number],
            check: roundToTwo(results.data?.hydraulicResult.shearStressInTheBasin) < stillingBasinSoilData.stillingBasin?.material.physicalData![0].allowShearStress!,
        },
        {
            label: t('output_hydraulic_vertical_stepped_sloped_allowable_shear_stress_lining'),
            symbol: <SmallLetter normal='τ' small='all' />,
            value: roundToTwo(stillingBasinSoilData.stillingBasin?.material.physicalData![0].allowShearStress),
            unit: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number],
            check: undefined,
        },
    ];

    let scour: IData[] = [
        {
            label: t('output_hydraulic_vertical_scour_location'),
            symbol: 'X',
            value: roundToTwo(results.data?.hydraulicResult.scourLocation),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
            check: undefined,
        },
    ];

    switch (type) {
        case EVerticalDropsTypes.UnlinedVerticalDrop:
            scour = [
                ...scourLocation,
                {
                    label: t('output_hydraulic_vertical_scour_depth'),
                    symbol: <SmallLetter normal='f' small='b' />,
                    value: roundToTwo(results.data?.hydraulicResult.scourDepth),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                    check: undefined,
                },
            ];
            break;
        case EVerticalDropsTypes.UnlinedPoolAndCountweir:
            scour = [
                {
                    label: t('output_hydraulic_vertical_scour_depth'),
                    symbol: <SmallLetter normal='f' small='b' />,
                    value: roundToTwo(results.data?.hydraulicResult.scourDepth),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                    check: undefined,
                },
                ...scourLocation,
                ...dissipationBasin,
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
            scour = [
                {
                    label: t('output_hydraulic_vertical_scour_depth'),
                    symbol: <SmallLetter normal='f' small='b' />,
                    value: roundToTwo(results.data?.hydraulicResult.scourDepth),
                    unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
                    check: undefined,
                },
                ...scourLocation,
                ...dissipationBasin,
                ...materialAndStress,
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
            scour = [...scourLocation, ...dissipationBasin, ...materialAndStress];
            break;

        default:
            break;
    }
    return (
        <div className='d-flex flex-column justify-content-center align-items-center gap-3 h-85 w-100 static-results'>
            <Table columns={columnsInput} data={scour} />
        </div>
    );
};

export default BasinDesignVertical;
